import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Picture from '../components/Picture';
import logo from '../assets/images/logoFree.webp';
import logoFallback from '../assets/images/logoFree.png';
import '../styles/ReviewFormPage.css';

const TOKEN_ENDPOINT = '/.netlify/functions/reviews-token';
const SUBMIT_ENDPOINT = '/.netlify/functions/reviews-submit';
const RATING_OPTIONS = [1, 2, 3, 4, 5];

const buildMessage = (reason) => {
    switch (reason) {
        case 'used':
            return 'Ce lien a deja ete utilise.';
        case 'expired':
            return 'Ce lien a expire.';
        case 'not_found':
            return 'Lien invalide.';
        default:
            return 'Lien invalide.';
    }
};

function ReviewFormPage() {
    const { token } = useParams();
    const [status, setStatus] = useState('loading');
    const [context, setContext] = useState('');
    const [error, setError] = useState('');
    const [expiresAt, setExpiresAt] = useState(null);
    const [rating, setRating] = useState(0);
    const [name, setName] = useState('');
    const [title, setTitle] = useState('');
    const [comment, setComment] = useState('');
    const [submitStatus, setSubmitStatus] = useState('idle');

    useEffect(() => {
        const checkToken = async () => {
            setStatus('loading');
            setError('');

            try {
                const response = await fetch(
                    `${TOKEN_ENDPOINT}?token=${encodeURIComponent(token)}`
                );

                if (!response.ok) {
                    throw new Error('Impossible de verifier le lien.');
                }

                const payload = await response.json();
                if (!payload.valid) {
                    setStatus('invalid');
                    setError(buildMessage(payload.reason));
                    setExpiresAt(null);
                    return;
                }

                const expires = payload.expiresAt
                    ? new Date(payload.expiresAt)
                    : null;
                if (expires && expires < new Date()) {
                    setStatus('invalid');
                    setError('Ce lien a expire.');
                    setExpiresAt(null);
                    return;
                }

                setContext(payload.context || '');
                setExpiresAt(expires ? expires.toISOString() : null);
                setStatus('ready');
            } catch (fetchError) {
                setError(fetchError.message);
                setStatus('error');
                setExpiresAt(null);
            }
        };

        if (token) {
            checkToken();
        } else {
            setStatus('invalid');
            setExpiresAt(null);
        }
    }, [token]);

    useEffect(() => {
        if (!expiresAt) {
            return;
        }

        const expiresAtMs = new Date(expiresAt).getTime();
        if (Number.isNaN(expiresAtMs)) {
            return;
        }

        const remaining = expiresAtMs - Date.now();
        if (remaining <= 0) {
            setStatus('invalid');
            setError('Ce lien a expire.');
            return;
        }

        const timer = setTimeout(() => {
            setStatus('invalid');
            setError('Ce lien a expire.');
        }, remaining);

        return () => clearTimeout(timer);
    }, [expiresAt]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (status !== 'ready') {
            return;
        }

        if (expiresAt && new Date(expiresAt) < new Date()) {
            setStatus('invalid');
            setError('Ce lien a expire.');
            return;
        }

        if (!rating || !comment.trim()) {
            setError('Merci de donner une note et un avis.');
            return;
        }

        setSubmitStatus('loading');
        setError('');

        try {
            const response = await fetch(SUBMIT_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    token,
                    rating,
                    name,
                    title,
                    comment
                })
            });

            if (!response.ok) {
                throw new Error('Impossible d\'envoyer votre avis.');
            }

            setSubmitStatus('done');
        } catch (submitError) {
            setError(submitError.message);
            setSubmitStatus('error');
        }
    };

    return (
        <div className='page review-page'>
            <main className='review-main'>
                <div className='review-form-card'>
                    <div className='review-brand'>
                        <Picture
                            webpSrc={logo}
                            fallbackSrc={logoFallback}
                            alt='MAF Industries'
                            className='review-logo'
                            imgClassName='review-logo-img'
                        />
                        <div>
                            <span className='review-title'>MAF Industries</span>
                            <span className='review-subtitle'>Avis client</span>
                        </div>
                    </div>

                    {status === 'loading' && (
                        <p className='review-message'>Chargement...</p>
                    )}

                    {(status === 'invalid' || status === 'error') && (
                        <div className='review-message'>
                            <p>{error}</p>
                            <a className='button button-outline' href='/'>
                                Retourner au site
                            </a>
                        </div>
                    )}

                    {status === 'ready' && submitStatus !== 'done' && (
                        <form className='review-form' onSubmit={handleSubmit}>
                            {context && (
                                <div className='review-context'>
                                    Avis pour : <strong>{context}</strong>
                                </div>
                            )}
                            <div className='review-field'>
                                <span>Note</span>
                                <div className='review-rating'>
                                    {RATING_OPTIONS.map((value) => (
                                        <button
                                            key={value}
                                            type='button'
                                            className={`rating-chip ${
                                                rating === value
                                                    ? 'is-active'
                                                    : ''
                                            }`}
                                            onClick={() => setRating(value)}
                                        >
                                            {value}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className='review-field'>
                                <span>Prenom (optionnel)</span>
                                <input
                                    type='text'
                                    value={name}
                                    onChange={(event) =>
                                        setName(event.target.value)
                                    }
                                    placeholder='Votre prenom'
                                />
                            </div>
                            <div className='review-field'>
                                <span>Titre (optionnel)</span>
                                <input
                                    type='text'
                                    value={title}
                                    onChange={(event) =>
                                        setTitle(event.target.value)
                                    }
                                    placeholder='Ex : Super experience'
                                />
                            </div>
                            <div className='review-field'>
                                <span>Votre avis</span>
                                <textarea
                                    rows='4'
                                    value={comment}
                                    onChange={(event) =>
                                        setComment(event.target.value)
                                    }
                                    placeholder='Ecrivez votre avis ici...'
                                />
                            </div>
                            {error && (
                                <p className='review-message'>{error}</p>
                            )}
                            <button
                                type='submit'
                                className='button'
                                disabled={submitStatus === 'loading'}
                            >
                                {submitStatus === 'loading'
                                    ? 'Envoi...'
                                    : 'Envoyer mon avis'}
                            </button>
                        </form>
                    )}

                    {submitStatus === 'done' && (
                        <div className='review-success'>
                            <h2>Merci pour votre avis.</h2>
                            <p>
                                Votre message a ete recu et sera verifie avant
                                publication.
                            </p>
                            <a className='button' href='/'>
                                Retourner au site
                            </a>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

export default ReviewFormPage;
